import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, ReplaySubject, tap, catchError, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _authService = inject(AuthService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);
    private _apiVer: BehaviorSubject<any> = new BehaviorSubject(null);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    get apiVer$(): Observable<any> {
        return this._apiVer.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) => {
                this._navigation.next(navigation);
            }),
            catchError(() => {
                const fallbackNavigation: Navigation = {
                    default: [],
                    compact: [],
                    futuristic: [],
                    horizontal: []
                };
                this._navigation.next(fallbackNavigation);
                return of(fallbackNavigation);
            })
        );
    }

    /**
     * Get API version from backend
     */
    getApiVer(): Observable<any> {
        const requestUrl = this._getBaseUrlAndPath('/getversion');
        const options = this._getRequestOptions();

        return this._httpClient.get<any>(requestUrl, options).pipe(
            tap((data) => {
                this._apiVer.next(data.version);
            }),
            catchError(() => {
                this._apiVer.next({ version: 'Error' });
                return of({ version: 'Error' });
            })
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _getBaseUrlAndPath(servicePath: string): string {
        if (environment.APP_PROXY) {
            return `${environment.PROXY_API_URL}${servicePath}`;
        } else {
            return `${environment.apiUrl}${servicePath}`;
        }
    }

    private _getRequestOptions(): { headers?: HttpHeaders, withCredentials?: boolean } {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });
        let withCreds = true;

        if (environment.APP_PROXY) {
            withCreds = false;
            const token = this._authService.proxyToken;
            if (token) {
                headers = headers.set('Authorization', `Bearer ${token}`);
            }
        }
        return { headers, withCredentials: withCreds };
    }
}
